import React, { SVGProps } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import Button from "../../../atoms/Button";
import IconArrow from "../../../assets/images/ic_arrow.png";
import IconSearch from "../../../assets/images/ic_search.png";
import { ReactComponent as IconStudent } from "../../../assets/svg/student.svg";
import { ReactComponent as IconGroup } from "../../../assets/svg/discussion.svg";
import { ReactComponent as IconTeachingMaterial } from "../../../assets/svg/book.svg";
import { classPath, Context, questionnairePath } from "../../../teacherStore";
import { IClass, IGroup, IStudent, ITeachingMaterial } from "../../../state";
import ModalConfirm from "../../../molecules/ModalConfirm";
import ModalEditGroup from "../../../molecules/teacher/ModalEditGroup";
import { generateFullName } from "../../../libs/Util";
import Modal from "../../../atoms/Modal";
import Tabs from "../../../atoms/Tabs";
import SwitchBox from "../../../molecules/teacher/SwitchBox";

import styles from "./DetailClass.module.scss";

export interface DetailClassProps {
  currentClass: IClass;
  groups: IGroup[];
  students: IStudent[];
  teaching_materials: ITeachingMaterial[];
  requestCreateGroupDone: boolean;
  calling: boolean;
  enabledSurvey: boolean;
  onCreateGroup: (classID: number, name: string) => void;
  changeNameOpenClose: (issue_id: number, opened: boolean) => void;
  changeAnswerOpenClose: (issue_id: number, opened: boolean) => void;
  onPublishMaterial: (teaching_material_id: number) => void;
  onUnPublishMaterial: (teaching_material_id: number) => void;
  onUpdateGroup: (group: IGroup) => void;
  onDeleteGroup: (groupId: number) => void;
  onPublishLesson: (lesson_id: number, class_id: number) => void;
  onUnPublishLesson: (lesson_id: number, class_id: number) => void;
}

const DetailClass: React.FC<DetailClassProps> = ({
  enabledSurvey,
  currentClass,
  groups,
  students,
  teaching_materials,
  requestCreateGroupDone,
  onCreateGroup,
  changeNameOpenClose,
  changeAnswerOpenClose,
  onPublishMaterial,
  onUnPublishMaterial,
  onUpdateGroup,
  onDeleteGroup,
  onPublishLesson,
  onUnPublishLesson,
  calling,
}) => {
  const [openModal, setOpenModal] = React.useState<{
    material: ITeachingMaterial | null;
    state: boolean;
  }>(() => ({
    material: null,
    state: false,
  }));
  const [openModalEditGroup, setOpenModalEditGroup] = React.useState<{
    group: IGroup | null;
    state: boolean;
  }>(() => ({
    group: null,
    state: false,
  }));

  const [listStudents, setListStudents] = React.useState<IStudent[]>([]);
  const [isShowModalDelete, setShowModalDelete] = React.useState(false);
  const [groupName, setGroupName] = React.useState("");
  const [listTabsTitle, setListTabsTitle] = React.useState<
    {
      title: string;
      icon: SVGProps<SVGSVGElement>;
      id: string;
    }[]
  >([]);

  const { state } = React.useContext(Context);
  const history = useHistory();

  React.useEffect(() => {
    setListStudents([...students]);
  }, [students]);

  React.useEffect(() => {
    setOpenModal({
      material: null,
      state: false,
    });
  }, [teaching_materials]);

  React.useEffect(() => {
    if (requestCreateGroupDone) setGroupName("");
  }, [requestCreateGroupDone]);

  React.useEffect(() => {
    const tabsTitle: {
      title: string;
      icon: SVGProps<SVGSVGElement>;
      id: string;
    }[] = [];
    tabsTitle.push(
      {
        title: "生徒の教材の開放",
        icon: <IconTeachingMaterial />,
        id: "teaching-material",
      },
      {
        title: "グループワークの\nグループ設定・編集",
        icon: <IconGroup />,
        id: "group",
      },
    );
    enabledSurvey &&
      tabsTitle.push({
        title: "生徒一覧\n事前/事後アンケート確認",
        icon: <IconStudent />,
        id: "student",
      });
    setListTabsTitle(tabsTitle);
  }, [enabledSurvey]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    if (searchValue.trim()) {
      const newList = students.filter(student => {
        const nameKanji = generateFullName([
          student.last_name,
          student.first_name,
        ]);
        const nameKana = generateFullName([
          student.last_name_kana,
          student.first_name_kana,
        ]);

        if (nameKanji.includes(searchValue) || nameKana.includes(searchValue)) {
          return student;
        }
        return false;
      });
      setListStudents([...newList]);
    } else {
      setListStudents([...students]);
    }
  };

  React.useEffect(() => {
    if (state.success_message) {
      setOpenModalEditGroup({
        group: null,
        state: false,
      });
    }
  }, [state.success_message]);

  const onGroupCreationSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onCreateGroup(currentClass.id, groupName);
    },
    [currentClass, groupName, onCreateGroup],
  );

  return (
    <div>
      <div className={styles.headerDetailClass}>
        <p>
          <span>クラス</span>
          <span>{currentClass ? currentClass.full_name : "---"}</span>
        </p>
      </div>
      <section className={styles.tabsHeader}>
        <div className={styles.infoClass}>
          <p>クラス名 :</p>
          <p>{currentClass ? currentClass.full_name : "---"}</p>
        </div>
        <div
          className={styles.infoClass}
          onClick={() => history.push(classPath)}
        >
          <p>＞クラス一覧に戻る</p>
        </div>
      </section>

      <Tabs listTabsTitle={listTabsTitle}>
        <section
          className={clsx(styles.section, "tab-pane active")}
          id="teaching-material"
        >
          <h5>このクラスに開放されている教科書の設定</h5>
          <div className={styles.detail}>
            <p>開放する教科書の設定と、</p>
            <p>回答した生徒名の公開/非公開を設定できます。</p>
            <p>各授業のメモは、メモアイコンをタップして閲覧できます。</p>
          </div>
          {teaching_materials
            .filter(material => !material.title.includes("オリエンテーション"))
            .map(material => (
              <div className={styles.PDF} key={material.id}>
                <div
                  className={clsx(
                    styles.PDF__label,
                    !material.opened && styles.enable,
                  )}
                  data-bs-toggle="collapse"
                  data-bs-target={`#PDF${material.id}`}
                  aria-expanded="false"
                  aria-controls={`PDF${material.id}`}
                >
                  <img src={material.image_url} alt="img1" />
                  <span
                    className={styles.title_lesson}
                  >{`${material.name} ${material.title}`}</span>
                  <img
                    className={styles.PDF__arrow}
                    src={IconArrow}
                    alt="ic_arrow"
                  />
                </div>
                <div
                  className={`collapse multi-collapse ${styles.wrapper_list}`}
                  id={`PDF${material.id}`}
                >
                  <div className={styles.PDF__status}>
                    <span>{material.name}を開放</span>
                    {!material.teacher_only && (
                      <Button
                        disabled={calling}
                        name={material.opened ? "開放中" : "開放する"}
                        variant={material.opened ? "normal" : "outline"}
                        onClick={() => setOpenModal({ state: true, material })}
                      />
                    )}
                  </div>
                  {material!
                    .lessons!.map(lesson => (
                      <div className={styles.lesson} key={lesson.id}>
                        <div className={styles.title}>
                          <p>{lesson.name}</p>
                          <p>{lesson.title}</p>
                          {!lesson.teacher_only && (
                            <Button
                              disabled={calling}
                              name={
                                lesson.published
                                  ? "Lesson開放中"
                                  : "Lessonを開放する"
                              }
                              variant={lesson.published ? "normal" : "outline"}
                              onClick={() =>
                                lesson.published
                                  ? onUnPublishLesson(
                                      lesson.id,
                                      currentClass.id,
                                    )
                                  : onPublishLesson(lesson.id, currentClass.id)
                              }
                            />
                          )}
                        </div>
                        <div className={styles.issue}>
                          {lesson
                            .issues!.sort((a, b) =>
                              a.page > b.page ? 1 : b.page > a.page ? -1 : 0,
                            )
                            .map(issue => (
                              <div className={styles.PDF__page} key={issue.id}>
                                <p>{`${issue.title}`}</p>
                                <div className={styles.wrapperSwitch}>
                                  <SwitchBox
                                    label="みんなの回答を公開"
                                    issue={issue}
                                    disabled={!lesson.published}
                                    onChange={(id, check) => {
                                      changeAnswerOpenClose(id, check);
                                    }}
                                  />
                                  <SwitchBox
                                    label="生徒名をクラスに公開"
                                    issue={issue}
                                    disabled={!lesson.published}
                                    onChange={(id, check) => {
                                      changeNameOpenClose(id, check);
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))
                    .flat()}
                </div>
              </div>
            ))}
        </section>

        <section className={clsx(styles.section, "tab-pane")} id="group">
          <h5 className="mb-4">クラスのグループ一覧</h5>
          <div className={styles.list}>
            {groups.map(group => (
              <div
                className={`${styles.list__item} ${styles.list__item__group}`}
                key={group.id}
                onClick={() => setOpenModalEditGroup({ group, state: true })}
              >
                <p>{group.name}</p>
              </div>
            ))}
          </div>
          <form className={styles.groupForm} onSubmit={onGroupCreationSubmit}>
            <label htmlFor="addName">グループ名を入力</label>
            <input
              type="text"
              id="addName"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              required
            />
            <Button name="グループを作成する" variant="normal" />
          </form>
        </section>
        {enabledSurvey && (
          <section className={clsx(styles.section, "tab-pane")} id="student">
            <h5 className="mb-2">クラスの生徒一覧</h5>
            <div className={styles.studentListExplain}>
              生徒名をクリックすると、アンケート結果を見ることができます。
            </div>
            <div className={styles.search}>
              <input type="text" onChange={handleSearch} />
              <img src={IconSearch} alt="" />
            </div>
            <div className={styles.list}>
              {listStudents.map(student => (
                <div
                  className={styles.list__item}
                  key={student.id}
                  onClick={() =>
                    history.push(`${questionnairePath}/${student.id}`, {
                      student,
                      currentClass,
                    })
                  }
                >
                  <div className={styles.list__item__name}>
                    <p>
                      {generateFullName([
                        student.last_name_kana,
                        student.first_name_kana,
                      ])}
                    </p>
                    <p>
                      {generateFullName([
                        student.last_name,
                        student.first_name,
                      ])}
                    </p>
                  </div>

                  <div className={styles.list__item__id}>
                    ID: {student.identity}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        <Button
          name="クラス一覧に戻る"
          variant={"normal"}
          className={styles.btnBackToClassList}
          onClick={() => history.push(classPath)}
        />
      </Tabs>

      {openModal.state && openModal.material && (
        <ModalConfirm
          onClose={() => setOpenModal({ state: false, material: null })}
          title={`${openModal.material.name}${
            openModal.material.opened
              ? "を非開放にしますか？"
              : "を開放しますか？"
          }`}
          buttons={[
            {
              title: openModal.material.opened ? "非開放にする" : "開放する",
              action: () => {
                if (openModal.material) {
                  const { opened, id } = openModal.material;
                  opened ? onUnPublishMaterial(id) : onPublishMaterial(id);
                }
              },
              type: openModal.material.opened ? "Danger" : "Success",
            },
            {
              title: "キャンセル",
              action: () => setOpenModal({ state: false, material: null }),
              type: "None",
            },
          ]}
        />
      )}
      {openModalEditGroup.state && (
        <ModalEditGroup
          group={openModalEditGroup.group}
          onSave={onUpdateGroup}
          handleDelete={() => setShowModalDelete(true)}
          onClose={() => setOpenModalEditGroup({ state: false, group: null })}
        />
      )}
      {isShowModalDelete && (
        <Modal
          onClose={() => setShowModalDelete(true)}
          className={styles.modalNotify}
        >
          <div className={styles.modalNotify__title}>
            グループを本当に削除しますか？
          </div>
          <div className={styles.modalNotify__actions}>
            <button
              className={styles.btnCancel}
              onClick={() => setShowModalDelete(false)}
            >
              キャンセル
            </button>
            <button
              className={styles.btnDelete}
              onClick={() => {
                onDeleteGroup(openModalEditGroup.group!.id);
                setShowModalDelete(false);
                setOpenModalEditGroup({ state: false, group: null });
              }}
            >
              削除
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DetailClass;
